import { inject, Injectable } from "@angular/core";
import moment from "moment";
import {
    asapScheduler,
    BehaviorSubject,
    from,
    Observable,
    Subject
} from "rxjs";
import { map } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMapGeometry } from "~app/common/components/map/map.settings";
import { Anskaffningsformer, VirkeskopKontraktstatus } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { RuleValidationMessage } from "~app/common/services/cqs/models/rule-validation-message";
import { DateUtils } from "~app/common/utils/date-utils";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { CreateAvtalCommand } from "../commands/avtal/CreateAvtalCommand";
import { UpdateAvtalCommand } from "../commands/avtal/UpdateAvtalCommand";
import { InvalidateAvtalCommand } from "../commands/cqs/InvalidateAvtalCommand";
import { ConvertKontraktVersionCommand } from "../commands/kontrakt/ConvertKontraktVersionCommand";
import { CopyKontraktCommand } from "../commands/kontrakt/CopyKontraktCommand";
import { CreateKontraktCommand } from "../commands/kontrakt/CreateKontraktCommand";
import { UpdateKontraktCommand } from "../commands/kontrakt/UpdateKontraktCommand";
import { Avtal, VKAvtalEditDTO, VKAvtalResponseDTO } from "../models/avtal-models";
import { ChangeObject } from "../models/change-object";
import { Kontrakt, VirkeskopKontraktstyp } from "../models/kontrakt-models";
import { AvtalService } from "../services/avtal.service";
import { SaveResult, VirkeskopValidationService } from "./validation/virkeskop-validation.service";

@Injectable()
export class AvtalMediatorService {
    readonly skogsvardBetalraderSaved = new Subject<void>();

    activeKontrakt$ = new BehaviorSubject<EntityDTO>(null);
    avtal$ = new BehaviorSubject<ChangeObject<Avtal>>(null);
    dirty$ = new BehaviorSubject<boolean>(false);
    error$ = new BehaviorSubject<any>(null);
    kontraktCreated$ = new BehaviorSubject<Kontrakt>(null);
    mapGoto$ = new BehaviorSubject<IMapGeometry>(null);

    private readonly onSaveCallbacks: OnSaveCallback[] = [];
    private readonly saveSrc = new Subject<void>();
    private readonly atgardSlutjusterad = new Subject<void>();
    private readonly authenticationService = inject(AuthenticationService);
    private readonly avtalService = inject(AvtalService);
    private readonly busyService = inject(BusyService);
    private readonly commandManager = inject(CommandManager);
    private readonly commandService = inject(CommandService);
    private readonly validationService = inject(VirkeskopValidationService);

    private avtalInternal: Avtal;
    private defaultKontrakt?: Kontrakt;

    constructor() {
        this.commandManager.onCommandReceived(() => {
            asapScheduler.schedule(() => {
                this.dirty$.next(this.commandManager.isDirty());
            });
        });
    }

    get avtal() {
        return this.avtalInternal;
    }

    get onSkogsvardBetalraderSaved$() {
        return this.skogsvardBetalraderSaved.asObservable();
    }

    get onAtgardSlutjusterad$() {
        return this.atgardSlutjusterad.asObservable();
    }

    get save$() { return this.saveSrc.asObservable(); }

    setAtgardSlutjusterad() {
        this.atgardSlutjusterad.next();
    }

    clearMapGoto(): void {
        this.mapGoto$.next(null);
    }

    convertVirkeskopskontrakt(kontrakt: Kontrakt) {
        this.commandManager.updateCommand(new ConvertKontraktVersionCommand(kontrakt.viol3, kontrakt.guid, this.avtalInternal.guid));
        this.save();
    }

    copyKontrakt(kontraktGuid: string) {
        this.commandManager.updateCommand(new CopyKontraktCommand(this.avtal.guid, kontraktGuid));
        const work = from(this.commandService.pushCommands(this.commandManager));
        this.busyService.addWork(work).subscribe(() => {
            this.saveSrc.next();
            this.refreshAvtalAfterSave();
        });
    }

    createAvtal(avtal: Avtal): void {
        this.commandManager.updateCommand(new CreateAvtalCommand(avtal.guid));
        this.validationService.validateOnChange(this.commandManager);
    }

    createKontrakt(avtal: Avtal, kontraktGuid: string, kontraktTyp: string, benamning?: string): Kontrakt {
        const kontrakt = new Kontrakt(kontraktGuid);
        kontrakt.benamning = benamning;
        if (kontraktTyp === VirkeskopKontraktstyp.SkogsvardFPV) {
            kontrakt.status = VirkeskopKontraktstatus.Kontrakt;
        } else {
            kontrakt.status = VirkeskopKontraktstatus.Anbud;
        }
        kontrakt.anbudGiltighetFrom = moment(new Date()).format(DateUtils.dateformat);
        kontrakt.anbudGiltighetTom = moment(new Date()).add(30, "days").format(DateUtils.dateformat);
        kontrakt.taxerasSomLantbruksenhet = true;
        kontrakt.kontraktTyp = kontraktTyp;
        kontrakt.viol3 = true;
        if (kontraktTyp === VirkeskopKontraktstyp.Skogsvard || kontraktTyp === VirkeskopKontraktstyp.SkogsvardFPV) {
            kontrakt.affarsform = null;
        }

        this.commandManager.updateCommand(new CreateKontraktCommand(kontrakt, avtal.guid));
        this.commandManager.updateCommand(new UpdateKontraktCommand(avtal.guid, kontrakt, false));
        this.validationService.validateOnChange(this.commandManager);
        return kontrakt;
    }

    getOrCreateDefaultKontrakt(avtal: Avtal, kontraktGuid: string, kontraktTyp: string, benamning?: string): Kontrakt {
        if (ObjectUtils.hasValue(this.defaultKontrakt)) {
            return this.defaultKontrakt;
        }
        this.defaultKontrakt = this.createKontrakt(avtal, kontraktGuid, kontraktTyp, benamning);
        return this.defaultKontrakt;
    }

    invalidateAvtal() {
        const avtal = this.avtalInternal;
        this.commandManager.updateMetaCommand(new InvalidateAvtalCommand(avtal.guid, avtal.id, avtal.rowVersion));
    }

    mapGoto(geometry: IMapGeometry): void {
        this.mapGoto$.next(geometry);
    }

    newAvtal(kontraktsTyp: string): void {
        const avtal = new Avtal(new VKAvtalResponseDTO());
        avtal.guid = GuidUtils.newGuid();
        const defaultAnsvarig = this.authenticationService.getUserData();
        avtal.benamning = "";
        avtal.anskaffningsform = Anskaffningsformer.Virkeskop;
        avtal.ansvarigVirkeskopare = {
            userName: defaultAnsvarig.userName,
            name: defaultAnsvarig.fullUserName
        };

        this.avtalInternal = avtal;
        this.avtal$.next(new ChangeObject<Avtal>(avtal, this.avtalInternal));

        this.createAvtal(this.avtalInternal);
        this.updateAvtal(this.avtalInternal);
        this.getOrCreateDefaultKontrakt(this.avtalInternal, GuidUtils.newGuid(), kontraktsTyp, "");
    }

    registerOnSaveCallback(validator: OnSaveCallback) {
        this.onSaveCallbacks.push(validator);
    }

    reloadAvtal() {
        return this.loadAvtalByGuid(this.avtalInternal.guid);
    }

    resetState() {
        this.defaultKontrakt = undefined;
        this.commandManager.clear();
        this.validationService.clear();
    }

    save(): Observable<unknown> {
        const working = new Subject<void>();
        this.onSaveCallbacks
            .forEach(validator => validator());
        this.validationService.validateOnSave().then(validateResult => {
            switch (validateResult) {
                case SaveResult.error:
                case SaveResult.declined:
                    working.error(validateResult);
                    return;
            }
            from(this.commandService.pushCommands(this.commandManager)).subscribe({
                next: () => {
                    this.commandManager.clear();
                    this.dirty$.next(this.commandManager.isDirty());
                    this.saveSrc.next();
                    this.reloadAvtal().subscribe({
                        next: () => working.next(),
                        error: (err) => working.error(err)
                    });
                },
                error: (err) => working.error(err)
            });
        });
        return working;
    }

    setActiveKontrakt(kontrakt: EntityDTO): void {
        if (this.activeKontrakt$.value !== kontrakt) {
            this.activeKontrakt$.next(kontrakt);
        }
    }

    setAvtal(id?: number): void {
        if (id) {
            this.loadAvtal(id);
        } else {
            this.avtal$.next(new ChangeObject<Avtal>(null, this.avtalInternal));
            this.avtalInternal = null;
        }
    }

    tryUpdateDefaultKontrakt(avtal: Avtal): void {
        if (ObjectUtils.hasValue(this.defaultKontrakt)) {
            this.changeKontraktTyp(avtal);
            this.commandManager.updateCommand(new UpdateKontraktCommand(avtal.guid, this.defaultKontrakt, true));
        }
    }

    updateAvtal(avtal: Avtal): void {
        const dto: VKAvtalEditDTO = {
            ...avtal,
            ansvarigVirkeskopare: avtal.ansvarigVirkeskopare?.userName,
            ansvarigSaljare: avtal.ansvarigSaljare?.userName
        };

        this.commandManager.updateCommand(new UpdateAvtalCommand(dto));
        this.validationService.validateOnChange(this.commandManager);
        this.invalidateAvtal();
        this.tryUpdateDefaultKontrakt(avtal);
    }

    validate(): Observable<RuleValidationMessage[]> {
        const worker = from(this.validationService.validateOnChange(this.commandManager));
        this.busyService.addWork(worker);
        return worker;
    }

    private changeKontraktTyp(avtal: Avtal): void {
        if (this.defaultKontrakt.kontraktTyp !== VirkeskopKontraktstyp.SkogsvardFPV) {
            if (avtal.isFirstDefaultKontraktSkogsvard) {
                this.defaultKontrakt.kontraktTyp = VirkeskopKontraktstyp.Skogsvard;
            } else if (!avtal.isFirstDefaultKontraktSkogsvard) {
                this.defaultKontrakt.kontraktTyp = VirkeskopKontraktstyp.Rundvirke;
            }
        }
    }

    private loadAvtal(id) {
        this.avtalService.getAvtal(id).subscribe(avtal => {
            this.onAvtalLoaded(avtal);
        });
    }

    private loadAvtalByGuid(guid): Observable<Avtal> {
        return this.avtalService.getAvtalByGuid(guid).pipe(map(avtal => this.onAvtalLoaded(avtal)));
    }

    private onAvtalLoaded(avtal: Avtal): Avtal {
        this.validationService.refreshSenasteHandelser(avtal.id);
        this.avtal$.next(new ChangeObject<Avtal>(avtal, this.avtalInternal));
        this.avtalInternal = avtal;
        avtal.validationMessages?.forEach(message => this.validationService.updateClientSideValidation(true, message));
        return avtal;
    }

    private refreshAvtalAfterSave() {
        this.commandManager.clear();
        this.dirty$.next(false);
        this.loadAvtalByGuid(this.avtalInternal.guid).subscribe();
    }
}

type OnSaveCallback = () => void;
